import React, { useEffect, useState, useRef, useReducer } from 'react';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';
import { Grid } from '_utils/components/Corporate/';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { mq } from '_utils/styles/';
import {
  enableBodyScroll,
  disableBodyScroll,
  buildSearchQuery,
  TagKey,
  queryFilterDelimiter,
  formatNumber
} from '_utils/helpers';
import { usePrevious } from '_utils/hooks';
import qs from 'query-string';
import { useTranslation } from 'react-i18next';

import {
  Bold,
  Container,
  Column,
  CrossWrapper,
  FiltersBody,
  FiltersButton,
  FiltersButtonCount,
  FiltersButtonLabel,
  FiltersClear,
  FiltersClose,
  FiltersColumn,
  FiltersContainer,
  FiltersFooter,
  FiltersForm,
  FiltersHeader,
  FiltersIconWrapper,
  FiltersSubmit,
  FiltersTitle,
  FiltersWrapper,
  Header,
  Heading,
  HeadingTerm,
  HeadingText,
  Layout,
  Meta,
  MetaControls,
  MetaInfo,
  NoResultsBody,
  NoResultsContainer,
  NoResultsHeading,
  NoResultsSuggestions,
  ResultItem,
  ResultsList
} from './StyledSearchResults';
import isDev from '_utils/helpers/dev';
import SearchResultCard from '_components/Corporate/SearchResultCard';
import Pagination from '_components/Corporate/Pagination';
// import mockResponse from './mockResponse.json';
import FiltersIcon from '_utils/icons/Filters';
import FocusTrap from 'focus-trap-react';
import LargeCross from '_utils/icons/LargeCross';
import FilterGroup from './FilterGroup';
import FilterContentType from './FilterContentType';
// import { normalizeId, equalIds } from '_utils/data';

let enquire;

if (typeof window !== 'undefined') {
  enquire = require('enquire.js');
}
/**
 * SearchResults component
 * parse search inputs from query string
 * submit search to api and display results
 */

type SearchResultsProps = {
  fields: any;
  sitecoreContext: any;
};

const NoResults: React.FC = () => {
  const [t] = useTranslation();

  let suggestions: string[];
  if (t('search-no-results-suggestions') === 'search-no-results-suggestions') {
    suggestions = [];
  } else {
    suggestions = t('search-no-results-suggestions').split('\n');
  }

  return (
    <NoResultsContainer>
      <NoResultsHeading>{t('search-no-results-heading')}</NoResultsHeading>
      <NoResultsBody>{t('search-no-results-body')}</NoResultsBody>
      {suggestions && (
        <NoResultsSuggestions>
          {suggestions.map((suggestion, index) => (
            <li key={index}>{suggestion}</li>
          ))}
        </NoResultsSuggestions>
      )}
    </NoResultsContainer>
  );
};

export type FilterGroupResult = {
  title: string;
  id: string;
  numResults: number;
};

export type FilterGroupMeta = {
  title: string;
  results: FilterGroupResult[];
};

export type Attribute = {
  type: string;
  value: string;
};

export type Image = {
  src: string;
  alt: string;
};

export type SearchResult = {
  title: string;
  url: string;
  description: string;
  category?: string;
  contentType?: string;
  attributes?: any[];
  image?: {
    src: string;
    alt: string;
  };
};

export type ContentTypeMeta = {
  title: string;
  description: string;
  id: string;
  numResults: number;
  filterGroups: FilterGroupMeta[];
};

export type FilterMeta = {
  title: string;
  id: string;
  numResults: number;
};

type TagFilters = {
  [key in TagKey]: string;
};

// actions for updating filters state
type Action =
  | { type: 'setContentType'; payload: { contentType: string } }
  | { type: 'setFilters'; payload: { filters: string } }
  | { type: 'setPage'; payload: { page: number } }
  | { type: 'setTerm'; payload: { term: string } }
  | { type: 'setTags'; payload: { tags: TagFilters } }
  | { type: 'clearFilters' }
  | {
      type: 'setAll';
      payload: {
        term: string;
        page: number;
        contentType: string;
        filters: string;
        tags: TagFilters;
      };
    };

type FiltersState = {
  term: string;
  page: number;
  contentType: string;
  filters: string;
  tags: TagFilters;
};

// function normalizeTags(tags) {
//   let normalized = {};

//   if (tags) {
//     const keys = Object.keys(tags);
//     keys.forEach((key) => {
//       if (tags.hasOwnProperty(key)) {
//         normalized[key] = normalizeId(tags[key]);
//       }
//     });
//   }

//   return normalized;
// }

// reducer for filters state
function filtersReducer(filtersState: FiltersState, action: Action) {
  let newState;

  switch (action.type) {
    case 'setContentType':
      // console.log('filtersReducer -> setContentType', action.payload);
      newState = {
        ...filtersState,
        contentType: action.payload.contentType,
        filters: '',
        page: 1
      };
      break;

    case 'setFilters':
      // console.log('filtersReducer -> setFilters', action.payload);
      newState = {
        ...filtersState,
        filters: action.payload.filters,
        page: 1
      };
      break;

    case 'setTerm':
      // console.log('filtersReducer -> setTerm');
      newState = {
        ...filtersState,
        term: action.payload.term,
        page: 1,
        filters: '',
        contentType: '',
        tags: {}
      };

      break;

    case 'setPage':
      // console.log('filtersReducer -> setPage');
      newState = {
        ...filtersState,
        page: action.payload.page
      };
      break;

    case 'setTags':
      // console.log('filtersReducer -> setTags');
      newState = {
        ...filtersState,
        tags: action.payload.tags
      };
      break;

    case 'clearFilters':
      // console.log('filtersReducer -> clearFilters');
      newState = {
        ...filtersState,
        page: 1,
        filters: '',
        contentType: '',
        tags: {}
      };
      break;

    case 'setAll': {
      // console.log('filtersReducer -> setAll');
      const { term, page, contentType, filters, tags } = action.payload;
      newState = {
        term,
        page,
        contentType: contentType || '',
        filters: filters || '',
        tags: tags || {}
      };
      break;
    }

    default:
      newState = filtersState;
  }

  return newState;
}

// return values for selected filter checkbox values
// passed in parentId restricts retrieval to
// filtergroup / tag type
function getSelectedFilterValues(parentId) {
  let values = [];
  let inputs: NodeList = document.querySelectorAll(
    `[data-parent="${parentId}"] [name="filterOption"]:checked`
  );

  [].forEach.call(inputs, (input) => {
    values.push((input as HTMLInputElement).value);
  });

  return values;
}

// get url query string with the current filters state
function getFiltersState({ term, page, contentType, filters, tags }: FiltersState): object {
  return {
    ...(term && { term }),
    page,
    ...(contentType && { type: contentType }),
    ...(filters && { filters }),
    ...tags
  };
}

const SearchResults: React.FC<SearchResultsProps> = ({ sitecoreContext }) => {
  const endpoint = '/api/v1/search';
  const [t] = useTranslation();
  const pageSize = 20;
  const location = useLocation();
  const navigate = useNavigate();
  const { search } = location || {};
  const searchParams = new URLSearchParams(search);
  const term = searchParams.get('term');

  // set initial state for filtersState reducer
  const [filtersState, dispatch] = useReducer(filtersReducer, {
    term,
    page: parseInt(searchParams.get('page')) || 1,
    contentType: searchParams.get('type') || '',
    filters: searchParams.get('filters') || '',
    tags: Object.keys(TagKey)
        .map((key) => TagKey[key]) // get enum vals
        .filter((value) => searchParams.get(value)) // filter by those in search params
        .reduce((tags, key) => ({ ...tags, [key]: searchParams.get(key) }), {}) // assemble tags obj
  });

  const filtersDialogRef = useRef();
  const filtersFormRef = useRef();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filtersDialogActive, setFiltersDialogActive] = useState(false);

  const filtersStateQuery = buildSearchQuery(getFiltersState(filtersState));

  // disable dialog at desktop layout (prevents focus trap staying active)
  useEffect(() => {
    if (enquire) {
      enquire.register(`screen and ${mq('lg', false)}`, {
        match: () => setFiltersDialogActive(false)
      });
    }
  }, []);

  // handle changes to url external to this component
  // e.g. browser back, user enters new search
  useEffect(() => {
    if (search && search.substr(1) === filtersStateQuery) {
      // search is same as filtersReducer state, no further action necessary
      return;
    }
    let values = qs.parse(search);
    const { term, page, type, filters } = values;
    dispatch({
      type: 'setAll',
      payload: {
        term: Array.isArray(term) ? term[0] : term || '',
        page:  parseInt(Array.isArray(page) ? page[0] : page) || 1,
        contentType: Array.isArray(type) ? type[0] : type || '',
        filters: Array.isArray(filters) ? filters[0] : filters || '',
        tags: Object.keys(TagKey)
        .map((key) => TagKey[key]) // get enum values
        .filter((value) => values[value]) // filter only those present in url
        .reduce((tags, key) => ({ ...tags, [key]: values[key] }), {}) // assemble tags obj
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Effect to SUBMIT SEARCH QUERY whenever filters state changes.
  // Also updates query string in url with current filter state
  useEffect(() => {
    if (!search) {
      return;
    }

    // if filtersReducer state differs from location.search, update history
    if (search && search.substr(1) !== filtersStateQuery) {
      navigate(`${location.pathname}?${filtersStateQuery}`);
    }

    setResponse(null);

    setError(null);

    setLoading(true);

    // build data for post
    let data = {
      ...(filtersState.term && { term: decodeURIComponent(filtersState.term) }),
      page: filtersState.page,
      pageSize,
      ...(filtersState.contentType && { type: filtersState.contentType }),
      ...(filtersState.filters && { filters: filtersState.filters.split(queryFilterDelimiter) })
    };

    // add tags
    Object.keys(TagKey)
      .filter((key: string) => filtersState.tags[TagKey[key]]) // filter by truthy values
      .forEach((key) => (data[key] = filtersState.tags[TagKey[key]].split(queryFilterDelimiter)));

    axios
      .post(endpoint, data)
      .then((response) => {
        if (response && response.data) {
          setResponse(response.data);
          setFiltersMeta(response.data.filters);
        }
      })
      .catch((err) => {
        // if (err && isDev) {
        //   // API not callable from localhost (TODO?), so trap error and fake response in dev
        //   setResponse(mockResponse);
        //   setFiltersMeta((mockResponse as any).filters);
        // } else {
        // production
        setError(err);
        // }
      })
      .then(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, filtersState]);

  // search results
  const items = response?.results || [];

  // only consider content types that have an id
  let contentTypes = filtersMeta?.contentTypes?.filter(({ id }) => !!id) || [];

  // auto insert 'All' as a content option (that matches empty contentType id)
  contentTypes.unshift({
    title: t('search-filters-all-content-types-label'),
    id: ''
  });

  const filteredContentTypes =
    filtersMeta?.contentTypes?.filter(({ id }) => id === filtersState.contentType) || [];

  const filterGroups = filteredContentTypes.length ? filteredContentTypes[0].filterGroups : [];

  const filterTags = filtersMeta?.tags || [];

  const { count } = response || 0;
  const numResults = items.length;
  const resultsStart = (filtersState.page - 1) * pageSize + (numResults ? 1 : 0);
  const resultsEnd = (filtersState.page - 1) * pageSize + numResults;
  const resultsName =
    count === 1 ? t('search-result-name-singular') : t('search-result-name-plural');
  const showRange = count > 1;

  let totalFiltersCount = 0;

  if (filtersState.contentType && filtersState.contentType !== '') {
    totalFiltersCount++;
  }

  if (filtersState.tags) {
    totalFiltersCount += Object.keys(filtersState.tags).reduce((total, key) => {
      let value = filtersState.tags[key];
      return value ? total + value.split(queryFilterDelimiter).length : total;
    }, 0);
  }

  if (filtersState.filters && filtersState.filters.length) {
    totalFiltersCount += filtersState.filters.split(queryFilterDelimiter).length;
  }

  let SearchMeta;

  if (loading) {
    SearchMeta = function renderLoading (){
      return (
        <Meta $my={1}>
          <Bold>Loading...</Bold>
        </Meta>
      );
    }
  } else if (error) {
    SearchMeta = function renderError () {
      return (
        <Meta $my={1}>{isDev ? `Error: ${error.message}` : t('search-error-message')}</Meta>
      );
    }
  } else if (response) {
    SearchMeta = function renderResponse () {
      return (
        <Meta $my={'4px'}>
          <MetaInfo as="h2" role="text">
            Showing{' '}
            {showRange && (
              <>
                <Bold>
                  {resultsStart}–{resultsEnd}
                </Bold>{' '}
                of{' '}
              </>
            )}
            <Bold>{formatNumber(count)}</Bold> {resultsName}
          </MetaInfo>
          <MetaControls>
            <FiltersButton
              onClick={() => setFiltersDialogActive(true)}
              className={totalFiltersCount > 0 ? 'active' : ''}
            >
              <FiltersIconWrapper>
                <FiltersIcon ariaHidden={true} />
              </FiltersIconWrapper>
              <FiltersButtonLabel>
                {t('search-filters-label')}{' '}
                {totalFiltersCount > 0 && (
                  <FiltersButtonCount>({totalFiltersCount})</FiltersButtonCount>
                )}
              </FiltersButtonLabel>
            </FiltersButton>
          </MetaControls>
        </Meta>
      );
    }
  } else {
    SearchMeta = null;
  }

  const prevFiltersDialogActive = usePrevious(filtersDialogActive);

  // disable body scroll when filters dialog opened
  useEffect(() => {
    const dialog = filtersDialogRef.current;

    if (filtersDialogActive && !prevFiltersDialogActive) {
      disableBodyScroll(dialog);
      document.body.classList.add('menu-active');
    } else if (!filtersDialogActive && prevFiltersDialogActive) {
      enableBodyScroll(dialog);
      document.body.classList.remove('menu-active');
    }

    // clean up
    return () => {
      enableBodyScroll(dialog);
      document.body.classList.remove('menu-active');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersDialogActive]);

  const onSubmitFilters = (e) => {
    if (e) {
      e.preventDefault();
      setFiltersDialogActive(false);
    }
  };

  const onContentTypeChange = (e) => {
    let form = filtersFormRef.current as any;

    if (!form) {
      return;
    }

    // Note old-school value retrieval to support IE11

    // filterContentType (radio buttons)
    let input: HTMLInputElement = document.querySelector('[name="filterContentType"]:checked');

    if (input) {
      if (input.value !== filtersState.contentType) {
        dispatch({
          type: 'setContentType',
          payload: { contentType: input.value }
        });
        return;
      }
    }
  };

  const onFiltersChange = (e) => {
    let form = filtersFormRef.current as any;

    if (!form) {
      return;
    }

    // filters (checkboxes)
    let values = getSelectedFilterValues('filters');

    dispatch({ type: 'setFilters', payload: { filters: values.join(queryFilterDelimiter) } });
  };

  const onFilterTagsChange = (e) => {
    let form = filtersFormRef.current as any;

    if (!form) {
      return;
    }

    // Note old-school value retrieval to support IE11

    const tags = filterTags
            .map(({ identifier }) => identifier) // get enum values
            .reduce(
              (tags, id) => ({
                ...tags,
                ...(getSelectedFilterValues(id).length && {
                  [id]: getSelectedFilterValues(id).join(queryFilterDelimiter)
                })
              }),
              {}
            ); // assemble tags obj

    dispatch({ type: 'setTags', payload: { tags } });
  };

  // handle out of bounds pagination
  useEffect(() => {
    if (response && count > 0) {
      let maxPage = Math.max(Math.ceil(count / pageSize), 1);
      if (filtersState.page > maxPage) {
        dispatch({ type: 'setPage', payload: { page: 1 } });
      }
    }

    // intentionally ignore count and filtersState.page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  /* DEV auto expand */
  // useEffect(() => {
  //   setFiltersDialogActive(true);
  // }, []);

  return (
    <Container>
      <Header>
        <Grid.Container>
          <Grid.Row $config={{ gutters: ['left', 'right'] }}>
            <Grid.Item $py={5} $config={{ col: { xs: 12 }, gutters: ['left', 'right'] }}>
              {term && (
                <Heading>
                  <HeadingText>{t('search-results-title')}</HeadingText>
                  <HeadingTerm>“{term}”</HeadingTerm>
                </Heading>
              )}
            </Grid.Item>
          </Grid.Row>
        </Grid.Container>
      </Header>
      <Grid.Container>
        <Layout $config={{ gutters: ['left', 'right'] }}>
          <FocusTrap
            active={filtersDialogActive}
            focusTrapOptions={{
              initialFocus: '[data-initfiltersfocus]'
            }}
          >
            <FiltersColumn
              as="section"
              aria-label={t('search-filters-accessible-filters-label')}
              className={filtersDialogActive ? 'filters-dialog-active' : ''}
              $config={{ col: { xs: 12, lg: 4 }, gutters: ['left', 'right'] }}
            >
              <FiltersWrapper>
                <FiltersHeader>
                  <FiltersClear
                    onClick={() => {
                      dispatch({ type: 'clearFilters' });
                    }}
                    aria-label={t('search-filters-accessible-clear-filters-label')}
                  >
                    {t('search-filters-clear-label')}
                  </FiltersClear>
                  <FiltersTitle id="filtersTitle">{t('search-filters-title')}</FiltersTitle>
                  <FiltersClose data-initfiltersfocus onClick={() => setFiltersDialogActive(false)}>
                    <CrossWrapper>
                      <LargeCross
                        title={t('search-filters-accessible-close-dialog-label')}
                        titleId={`search-filters-close`}
                        ariaHidden={true}
                      />
                    </CrossWrapper>
                  </FiltersClose>
                </FiltersHeader>
                <FiltersForm onSubmit={onSubmitFilters} ref={filtersFormRef}>
                  <FiltersContainer>
                    <FiltersBody ref={filtersDialogRef}>
                      {contentTypes && (
                        <FilterContentType
                          contentTypes={contentTypes}
                          selectedContentTypeFilter={filtersState.contentType}
                          onChange={onContentTypeChange}
                        />
                      )}
                      {filterGroups &&
                        filterGroups.map((filterGroup, index) => (
                          <FilterGroup
                            key={`FilterGroup-${index}`}
                            parentId="filters"
                            filterGroup={filterGroup}
                            selectedFilters={
                              filtersState.filters
                                ? filtersState.filters.split(queryFilterDelimiter)
                                : []
                            }
                            onChange={onFiltersChange}
                          />
                        ))}
                      {filterTags &&
                        filterTags.map((filterTagGroup, index) => (
                          <FilterGroup
                            key={`FilterTagGroup-${index}`}
                            parentId={filterTagGroup.identifier}
                            filterGroup={filterTagGroup}
                            selectedFilters={
                              filtersState.tags[filterTagGroup.identifier]
                                ? filtersState.tags[filterTagGroup.identifier].split(
                                    queryFilterDelimiter
                                  )
                                : []
                            }
                            onChange={onFilterTagsChange}
                          />
                        ))}
                    </FiltersBody>
                    <FiltersFooter>
                      <FiltersSubmit type="submit">
                        {t('search-filters-show-results-label-prefix')} ({count})
                      </FiltersSubmit>
                    </FiltersFooter>
                  </FiltersContainer>
                </FiltersForm>
              </FiltersWrapper>
            </FiltersColumn>
          </FocusTrap>
          {/* search results */}
          <Column
            as="section"
            aria-label={t('search-results-accessible-results-label')}
            $config={{ col: { xs: 12, lg: 8 }, gutters: ['left', 'right'] }}
          >
            {SearchMeta && <SearchMeta />}
            {response && response.results && response.results.length ? (
              <ResultsList $justifyContent="center">
                {response.results.map((result, index) => (
                  <ResultItem
                    as="li"
                    key={index}
                    aria-label={`${t(
                      'search-results-accessible-result-label-prefix'
                    )} ${resultsStart + index} ${t(
                      'search-results-accessible-total-prefix'
                    )} ${count}`}
                    className={
                      count < pageSize && index === response.results.length - 1 ? 'no-border' : ''
                    }
                  >
                    <SearchResultCard
                      result={result}
                      hostName={sitecoreContext.siteInfo.siteBaseUrl}
                    />
                  </ResultItem>
                ))}
              </ResultsList>
            ) : (
              !loading && <NoResults />
            )}
            {/* pagination */}
            <Grid.Row $justifyContent="center" $config={{ gutters: ['left', 'right'] }} $py={2}>
              {count > pageSize && !loading && (
                <Pagination
                  count={count}
                  pageIndex={filtersState.page}
                  baseUrl={`${location.pathname}?${filtersStateQuery}`}
                  pageSize={pageSize}
                  // override Pagination component's native links to make filtersState source of truth
                  onNavigate={(page) => {
                    dispatch({ type: 'setPage', payload: { page } });
                  }}
                  ariaLabel={t('search-results-accessible-pagination-label')}
                />
              )}
            </Grid.Row>
          </Column>
        </Layout>
      </Grid.Container>
    </Container>
  );
};

export default compose(withSitecoreContext())(SearchResults);
